import { CartAddEvent, CartUpdateEvent } from '@theme/events';

/**
 * Handle proizvoda koji se prodaje samo uz uslov (vidi require_other_product
 * u snippets/fcd-add-to-cart-button.liquid). Trenutno postoji samo jedan
 * ovakav proizvod u temi (Nightguard po sniznoj ceni).
 */
const REQUIRE_OTHER_PRODUCT_HANDLES = ['nightguards-special-offer'];

function parseVariantId(rawVariantId) {
  const normalized = rawVariantId?.trim();
  if (!normalized) return null;

  if (!/^\d+$/.test(normalized)) return null;

  const variantId = Number(normalized);
  if (!Number.isSafeInteger(variantId) || variantId <= 0) return null;

  return variantId;
}

function showButtonError(button, message) {
  if (!button.dataset.originalLabel) {
    button.dataset.originalLabel = button.textContent?.trim() || '';
  }

  button.textContent = message;
  button.classList.add('fcd-add-to-cart-button--error');

  window.setTimeout(() => {
    button.textContent = button.dataset.originalLabel || button.textContent;
    button.classList.remove('fcd-add-to-cart-button--error');
    button.disabled = false;
  }, 3000);
}

function getCartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((item) => (item instanceof HTMLElement ? item.dataset.sectionId : ''))
    .filter(Boolean)
    .join(',');
}

/**
 * @param {number} variantId
 */
async function addVariantToCart(variantId) {
  /** @type {{ items: Array<{ id: number, quantity: number }>, sections?: string, sections_url?: string }} */
  const payload = {
    items: [{ id: variantId, quantity: 1 }],
  };

  const sections = getCartSectionIds();
  if (sections) {
    payload.sections = sections;
    payload.sections_url = window.location.pathname;
  }

  const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });

  let data = null;

  try {
    data = await response.json();
  } catch (error) {
    throw new Error(`HTTP ${response.status}`);
  }

  if (!response.ok || data?.status) {
    const message = data?.description || data?.message || `HTTP ${response.status}`;
    throw new Error(message);
  }

  return data;
}

async function fetchCart() {
  const response = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) return null;

  return response.json();
}

/**
 * @param {HTMLElement} button
 * @param {number} variantId
 * @param {Record<string, unknown>} addResponse
 */
async function handleAddSuccess(button, variantId, addResponse) {
  const cart = await fetchCart();

  document.dispatchEvent(
    new CartAddEvent(cart ?? addResponse, button.id, {
      source: 'fcd-add-to-cart-button',
      itemCount: cart?.item_count ?? 1,
      productId: button.dataset.productId,
      variantId: String(variantId),
      sections: addResponse.sections,
    })
  );

  const redirectUrl = button.dataset.redirectUrl?.trim();
  if (redirectUrl) {
    window.location.href = redirectUrl;
    return;
  }

  const drawer = document.getElementById('site-cart-drawer');
  if (drawer && 'open' in drawer && typeof drawer.open === 'function') {
    drawer.open();
  }

  button.disabled = false;
}

/**
 * Da li korpa zadovoljava uslov kupovine za dato dugme.
 * Vidi komentar u snippets/fcd-add-to-cart-button.liquid.
 *
 * @param {HTMLElement} button
 * @param {{ items?: Array<{ handle?: string, product_id?: number }> } | null} cart
 */
function cartMeetsRequirement(button, cart) {
  if (button.dataset.requireOther !== 'true') return true;

  const items = cart?.items ?? [];
  if (!items.length) return false;

  const handles = (button.dataset.requireHandles || '')
    .split(',')
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean);

  if (handles.length) {
    return items.some((item) => handles.includes(String(item.handle || '').toLowerCase()));
  }

  const ownProductId = button.dataset.productId;
  return items.some((item) => String(item.product_id) !== String(ownProductId));
}

/**
 * @param {string} lineKey
 */
async function removeCartLine(lineKey) {
  const payload = { id: lineKey, quantity: 0 };

  const sections = getCartSectionIds();
  if (sections) {
    payload.sections = sections;
    payload.sections_url = window.location.pathname;
  }

  const response = await fetch(window.Theme?.routes?.cart_change_url || '/cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) return null;

  return response.json();
}

/**
 * Uklanja iz korpe proizvode sa uslovom kupovine (require_other_product) cim
 * uslov vise nije ispunjen - npr. kupac je uklonio mouthguard posto je vec
 * dodao Nightguard po snizenoj ceni. Bez ovoga takav Nightguard ostaje u
 * korpi po promo ceni bez ikakvog drugog proizvoda uz njega.
 *
 * @param {{ items?: Array<{ key: string, handle?: string }> } | null} cart
 */
async function enforceCartRequirements(cart) {
  const items = cart?.items ?? [];
  if (!items.length) return;

  for (const item of items) {
    const handle = String(item.handle || '').toLowerCase();
    if (!REQUIRE_OTHER_PRODUCT_HANDLES.includes(handle)) continue;

    const hasQualifyingItem = items.some(
      (other) => other !== item && String(other.handle || '').toLowerCase() !== handle
    );

    if (!hasQualifyingItem) {
      const updatedCart = await removeCartLine(item.key);
      if (updatedCart) {
        document.dispatchEvent(
          new CartUpdateEvent(updatedCart, 'fcd-cart-requirement', {
            itemCount: updatedCart.item_count,
            sections: updatedCart.sections,
          })
        );
      }
    }
  }
}

/** Uskladi vidljivost svih dugmadi i sadrzaj korpe sa uslovom kupovine. */
async function refreshFcdRequirements() {
  const cart = await fetchCart();
  if (!cart) return;

  await enforceCartRequirements(cart);

  const buttons = Array.from(document.querySelectorAll('.fcd-add-to-cart-button[data-require-other="true"]'));
  buttons.forEach((button) => {
    if (!(button instanceof HTMLElement)) return;
    button.hidden = !cartMeetsRequirement(button, cart);
  });
}

function initFcdAddToCartButtons() {
  document.querySelectorAll('.fcd-add-to-cart-button[data-variant-id]').forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;
    if (button.dataset.fcdCartAddInit === 'true') return;

    const variantId = parseVariantId(button.dataset.variantId);
    if (!variantId) return;

    button.dataset.fcdCartAddInit = 'true';

    button.addEventListener('click', async (event) => {
      event.preventDefault();

      button.disabled = true;

      try {
        /* Ponovna provera pred samo dodavanje - korpa se mogla promeniti u drugom tabu */
        if (button.dataset.requireOther === 'true') {
          const currentCart = await fetchCart();
          if (!cartMeetsRequirement(button, currentCart)) {
            button.hidden = true;
            button.disabled = false;
            return;
          }
        }

        const data = await addVariantToCart(variantId);
        await handleAddSuccess(button, variantId, data);
      } catch (error) {
        const fallbackMessage = button.dataset.errorLabel?.trim() || 'Could not add to cart';
        const message = error instanceof Error && error.message ? error.message : fallbackMessage;

        console.error('[fcd-add-to-cart-button]', {
          variantId,
          productId: button.dataset.productId || null,
          error: message,
        });

        showButtonError(button, message);
      }
    });
  });
}

function bootFcdAddToCartButtons() {
  initFcdAddToCartButtons();
  refreshFcdRequirements();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootFcdAddToCartButtons);
} else {
  bootFcdAddToCartButtons();
}

document.addEventListener('shopify:section:load', bootFcdAddToCartButtons);

/* Korpa se menja i van ovog dugmeta - drawer, uklanjanje stavke, drugi tab */
['cart:update', 'cart:refresh', 'CartUpdateEvent', 'CartAddEvent'].forEach((name) => {
  document.addEventListener(name, refreshFcdRequirements);
});

window.addEventListener('pageshow', refreshFcdRequirements);
document.addEventListener('visibilitychange', () => {
  if (!document.hidden) refreshFcdRequirements();
});
